import { cloneDeep } from 'lodash'
import store from '@store/index'
import axios from 'axios'
import { axiosInstance } from '@api/rest.service'

export default class RestService {
  constructor(namespace) {
    this.namespace = namespace ? `${process.env.VUE_APP_API_PREFIX}/${namespace}` : `${process.env.VUE_APP_API_PREFIX}`
  }

  get(params) {
    let url = params && params.id ? `${this.namespace}/${params.id}` : this.namespace
    if (params && params.context) {
      let q = this.makeRequestParams(params.context)
      url += `?${q}`
    }
    return this.makeRequest('get', url)
  }

  create(params) {
    let url = params.context ? `${this.namespace}?context=${params.context}` : this.namespace
    return this.makeRequest('post', url, params.value)
  }

  update(params, method = 'put') {
    let url = params.id ? `${this.namespace}/${params.id}` : this.namespace
    return this.makeRequest(method, url, params.value)
  }

  updateWithPost(params) {
    return this.makeRequest('post', `${this.namespace}/${params.id}`, params.value)
  }

  patch(params) {
    let url = params.id ? `${this.namespace}/${params.id}` : this.namespace
    let data = params.value || params
    return this.makeRequest('patch', url, data)
  }

  copy(params) {
    return this.makeRequest('post', `${this.namespace}/${params.id}/copy`, params.value)
  }

  delete(params) {
    let url = params.replaceWith
      ? `${this.namespace}/${params.id}?replace_with=${params.replaceWith}`
      : `${this.namespace}/${params.id}`
    return this.makeRequest('delete', url)
  }

  getCreateFormData() {
    return this.makeRequest('get', `${this.namespace}/create`)
  }

  getInlineEditingData() {
    return this.makeRequest('get', `${this.namespace}`)
  }

  getDeleteConfirmation(params) {
    return this.makeRequest('get', `${this.namespace}/${params.id}/delete-confirmation`)
  }

  download(url) {
    const originalHeaders = cloneDeep(axiosInstance.defaults.headers)
    let headers = {}
    headers['Content-Type'] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    axiosInstance.defaults.headers = headers
    axiosInstance.defaults.responseType = 'arraybuffer'
    return new Promise(resolve => {
      return this.makeRequest('get', `${this.namespace}/download/${url}`).then(response => {
        axiosInstance.defaults.headers = originalHeaders
        delete axiosInstance.defaults.responseType
        resolve(response)
      })
    })
  }

  downloadMedia(id) {
    const originalHeaders = cloneDeep(axiosInstance.defaults.headers)
    let headers = {}
    axiosInstance.defaults.headers = headers
    axiosInstance.defaults.responseType = 'arraybuffer'
    return new Promise(resolve => {
      return this.makeRequest('get', `${this.namespace}/download/media/${id}`).then(response => {
        axiosInstance.defaults.headers = originalHeaders
        delete axiosInstance.defaults.responseType
        resolve(response)
      })
    })
  }

  downloadWithParams(params) {
    const originalHeaders = cloneDeep(axiosInstance.defaults.headers)
    let headers = {}
    headers['Content-Type'] = 'application/json'
    axiosInstance.defaults.headers = headers
    axiosInstance.defaults.responseType = 'arraybuffer'
    return new Promise(resolve => {
      return this.makeRequest('post', `${this.namespace}`, params).then(response => {
        axiosInstance.defaults.headers = originalHeaders
        delete axiosInstance.defaults.responseType
        resolve(response)
      })
    })
  }

  submitDecisions(params) {
    return this.makeRequest('post', this.namespace, params.value)
  }

  makeRequest(requestType, url, payload = null) {
    let tokenSource
    if (store.state.requestCancellationToken) {
      tokenSource = store.state.requestCancellationToken
    } else {
      const CancelToken = axios.CancelToken
      this.source = CancelToken.source()
      // OMG! AXIOS has ISSUES! it merges the cancel token incorrectly, so we trick it and move it from the prototype to the object itself
      // noinspection SillyAssignmentJS
      this.source.token.throwIfRequested = this.source.token.throwIfRequested
      // the above copies the property from the prototype to the this, so the extend function will also copy it because probably the extends using hasOwnProperty
      this.source.token.promise.then = this.source.token.promise.then.bind(this.source.token.promise)
      this.source.token.promise.catch = this.source.token.promise.catch.bind(this.source.token.promise)
      tokenSource = this.source
      store.commit('updateRequestCancellationToken', tokenSource)
    }

    let opts = {
      method: requestType,
      url: url,
      data: payload,
      cancelToken: tokenSource.token
    }
    return axiosInstance(opts)
  }

  makeRequestParams(a) {
    let prefix, s, add, name, r20, output
    s = []
    r20 = /%20/g
    add = function(key, value) {
      if (value === null) return
      // If value is a function, invoke it and return its value
      value = typeof value === 'function' ? value() : value === null ? '' : value
      s[s.length] = encodeURIComponent(key) + '=' + encodeURIComponent(value)
    }
    if (a instanceof Array) {
      for (name in a) {
        add(name, a[name])
      }
    } else {
      for (prefix in a) {
        this.buildParams(prefix, a[prefix], add)
      }
    }
    output = s.join('&').replace(r20, '+')
    return output
  }

  buildParams(prefix, obj, add) {
    let name, i, l, rbracket
    rbracket = /\[\]$/
    if (obj instanceof Array) {
      for (i = 0, l = obj.length; i < l; i++) {
        if (rbracket.test(prefix)) {
          add(prefix, obj[i])
        } else {
          this.buildParams(prefix + '[' + (typeof obj[i] === 'object' ? i : '') + ']', obj[i], add)
        }
      }
    } else if (typeof obj === 'object') {
      // Serialize object item.
      for (name in obj) {
        this.buildParams(prefix + '[' + name + ']', obj[name], add)
      }
    } else {
      // Serialize scalar item.
      add(prefix, obj)
    }
  }
}
